/**
 * The format block: what KIND of value each of the panel's columns holds, and therefore how every
 * surface prints it. It declares no display type of its own — it is a block, not a renderer — and
 * unlike the other blocks it is not gated by `carriedBy`, because there is no display type that
 * shows no values.
 *
 * The rule it exists to enforce: A TYPE BELONGS TO A COLUMN, NOT TO A SLOT. An axis, a hover-card
 * line, a table cell and a tile are all views of the same column; each carrying its own type is four
 * places to disagree about one fact. So there is no axisType and no tooltipValueType, and there must
 * never be one — a slot INHERITS from the column it draws. Full reasoning, the migration of the four
 * carriers that predate this, and the invariants:
 * docs/cirra/canon/product/analytics/analytics-value-formatting-contract.md
 *
 * Keyed by column name rather than declared per key-in-block so that adding a display type costs
 * nothing here: every type, including types that do not exist yet, carries this block. What each
 * renderer must still do is READ the resolved types (resolveAnalyticsPanelValueTypes) instead of
 * printing raw values — the schema is type-agnostic, the drawing never can be.
 */
import { ANALYTICS_VALUE_COLUMN_TYPES, normalizeAnalyticsColumnType } from '../../../cirra/analyticsColumnTypes.js';

/* The one vocabulary, less `html`. An html column is not a kind of VALUE — it says the transform
   returns markup for that cell, which is compiled and validated when the panel cooks and only a
   table draws. It stays where it is, on `columns`. */
export const ANALYTICS_VALUE_FORMAT_TYPES = ANALYTICS_VALUE_COLUMN_TYPES.filter((type) => type !== 'html');

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/* The merge, hand-written because the block is a map: a caller types ONE column without resending
   the others, which is the opposite of the full-replacement lists elsewhere and the right default
   for a map whose keys are the panel's own columns. '' takes a column's type back off — the same
   thing '' means to a clearable field. An unrecognized type keeps what is stored rather than failing
   the whole panel save, which is the tolerance mergeAnalyticsConfigBlock's enum merger has. */
export const mergeAnalyticsValueFormat = ({ requested, stored } = {}) => {
  const merged = {};

  for (const [key, type] of Object.entries(isPlainObject(stored) ? stored : {})) {
    const normalized = normalizeAnalyticsColumnType(type);

    if (key && ANALYTICS_VALUE_FORMAT_TYPES.includes(normalized)) {
      merged[key] = normalized;
    }
  }

  for (const [key, type] of Object.entries(isPlainObject(requested) ? requested : {})) {
    if (!key) {
      continue;
    }

    if (type === '' || type === null) {
      delete merged[key];
      continue;
    }

    const normalized = normalizeAnalyticsColumnType(type);

    if (ANALYTICS_VALUE_FORMAT_TYPES.includes(normalized)) {
      merged[key] = normalized;
    }
  }

  return merged;
};

/* Display code returns the whole config fresh each resolution, so an unrecognized type there is a
   typo rather than a stale store — it is refused by name, like every other returned setting the
   renderer would not act on. */
export const validateAnalyticsValueFormat = (block) => {
  if (!isPlainObject(block)) {
    return 'format must be an object keyed by column name, like { "revenue": "currency" }.';
  }

  for (const [key, type] of Object.entries(block)) {
    if (!key) {
      return 'Every format key must be a column name.';
    }

    if (typeof type !== 'string' || !ANALYTICS_VALUE_FORMAT_TYPES.includes(normalizeAnalyticsColumnType(type))) {
      return `format does not accept "${type}" for "${key}". Allowed: ${ANALYTICS_VALUE_FORMAT_TYPES.join(', ')}.`;
    }
  }

  return '';
};

const format = {
  /* No names: a block, not a renderer. */
  names: [],
  block: 'format',
  /* Not one type's settings, so the tool declares it by hand beside the other every-panel
     properties and the per-type loops skip it — the same standing `columns` has. No `carriedBy`:
     every type draws values, so there is nothing to gate. */
  sharedBlock: true,
  /* No `fields`: the block is a MAP keyed by the panel's own column names, which the field-spec
     vocabulary (fixed names, one kind each) has no way to describe. Its merge and its validation are
     hand-written for that reason, next to the columns block's, which is hand-written for its own. */
  /* No `stateSummary`: those are clauses on the `- Display:` line, walked one block per TYPE, and
     this block belongs to no type. The panel state prints value types on their own line, from the
     RESOLVED map rather than from this block — a stat tile's valueType is the same fact stored
     elsewhere, and the assistant needs to see it whichever block it came from. */
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: { type: ['string', 'null'], enum: [...ANALYTICS_VALUE_FORMAT_TYPES, null] },
    description: `What KIND of value each column holds, keyed by column name — {"revenue": "currency", "day": "date", "ctr": "percent"}. THE answer to "show this as money / as a percentage / with a $", on ANY display type: the chart's value axis, its hover card, the table cell and the tile all read this one declaration, so a column typed once reads the same everywhere it is drawn. Allowed types: ${ANALYTICS_VALUE_FORMAT_TYPES.join(', ')}. Emit RAW numbers from SQL and declare the type here — never format a value into a string yourself (no hand-written currency symbols, percent signs, or thousands separators), and never multiply a rate by 100 to make the axis read right. Merged over what is stored, so you can type one column without resending the others; send "" for a column to take its type back off. A key naming a column the panel does not draw is inert.`
  }
};

export default format;
